/**
 * Os formatadores que a interface inteira usa, num lugar só.
 *
 * Segue o precedente que `format-currency.ts` já registra, e pelo
 * mesmo motivo que ele descreve: `formatCena` morava colado no
 * `CenaGauge` e acabou COPIADO no `DashboardSummaryCards`, porque quem
 * precisou dele numa segunda tela achou mais fácil copiar do que
 * importar de um componente. Em 2026-09-02 a conta era maior — o mesmo
 * `Intl.NumberFormat("pt-BR")` declarado em nove arquivos, o mesmo
 * `DateTimeFormat` de dia/mês em cinco, e o CENA escrito de QUATRO
 * jeitos, um deles com ponto decimal.
 *
 * **Os objetos `Intl` nascem uma vez, no módulo.** Construí-los por
 * chamada é o caso do `shared/types/mention.ts`, que criava um
 * `DateTimeFormat` novo por menção da lista — o construtor do `Intl` é
 * a parte cara, o `.format` é barata.
 *
 * **`null` é ausência e vira "—", nunca zero.** É a mesma convenção do
 * dinheiro e a mesma do back: menção neutra sem peso cadastrado, post
 * de rede antes do relatório e valoração de rede são todos "não se
 * aplica", e um `if (!value)` colapsaria isso com o zero legítimo.
 *
 * **Não confundir com `date-range.ts`.** Lá o assunto é a JANELA de
 * filtro, que trabalha em relógio de parede e não converte fuso
 * (decisão de 2026-08-29). Aqui é só apresentação de um `Date` que
 * quem chama já resolveu.
 */

/** O que aparece no lugar de um valor ausente, na interface inteira. */
export const AUSENTE = "—";

const inteiro = new Intl.NumberFormat("pt-BR");

const compacto = new Intl.NumberFormat("pt-BR", {
  notation: "compact",
  maximumFractionDigits: 1
});

const umaCasa = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const diaMes = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short"
});

const diaMesAno = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
  year: "numeric"
});

const diaMesHora = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit"
});

/** "1.234" — contagem, total, qualquer inteiro. */
export function formatInteger(value: number | null | undefined): string {
  return value == null ? AUSENTE : inteiro.format(value);
}

/** "1,2 mil" — pra célula que divide espaço com outra coisa. Mesma
 *  convenção de abreviação do dinheiro compacto, de propósito: duas
 *  numa fileira de cards leriam como erro. */
export function formatCompact(value: number | null | undefined): string {
  return value == null ? AUSENTE : compacto.format(value);
}

const compactoComSinal = new Intl.NumberFormat("pt-BR", {
  notation: "compact",
  maximumFractionDigits: 1,
  signDisplay: "exceptZero"
});

/** "+1,2 mil" / "-1,2 mi" — compacto com sinal explícito, pra número que é
 *  saldo (soma que pode ser negativa e não tem teto). O sinal do Intl é o
 *  hífen ASCII, e o espaço antes de "mil"/"mi" é NBSP. Zero sai "0". */
export function formatSignedCompact(value: number | null | undefined): string {
  return value == null ? AUSENTE : compactoComSinal.format(value);
}

/** "155k" — o número de um SELO (o contador do menu lateral), onde cabem
 *  quatro caracteres e "155,4 mil" não cabe (2026-10-18, pedido da dona:
 *  "155366" saía cortado). Milhar vira `k` sem decimal a partir de 10 mil
 *  ("1,6k", "155k"); milhão segue a convenção da casa ("1,2 mi"); abaixo
 *  de mil é o inteiro. O exato vai no `title`. */
export function formatBadgeCount(value: number): string {
  if (value < 1_000) return inteiro.format(value);
  if (value < 1_000_000) {
    const milhares = value / 1_000;
    // `inteiro` aceita três casas por padrão: sem o arredondar, 154.208 saía "154,208k".
    return `${value < 10_000 ? umaCasa.format(milhares) : inteiro.format(Math.round(milhares))}k`;
  }
  return compacto.format(value);
}

/** "7,4" — o CENA e qualquer número de uma casa decimal.
 *
 *  Era `toFixed(1)` no detalhe da menção, que escreve "7.4" com PONTO:
 *  o único lugar da interface fora do pt-BR. */
export function formatDecimal(value: number | null | undefined): string {
  return value == null ? AUSENTE : umaCasa.format(value);
}

/** A nota CENA, sempre com uma casa.
 *
 *  `null` é SEM NOTA, e não zero: menção neutra sem peso cadastrado
 *  (`fav_neutral` NULL) e publicação de rede antes do relatório são as
 *  duas fontes, e as duas são "não se aplica". O 5,0 acidental da
 *  reescala nasceu exatamente de colapsar isso.
 *
 *  Existia em DOIS lugares com implementações diferentes — uma no
 *  `CenaGauge` e outra copiada no `DashboardSummaryCards` —, e um
 *  terceiro ponto (o detalhe da menção) escrevia `toFixed(1)`, com
 *  ponto decimal. */
export function formatCena(value: number | null | undefined): string {
  return formatDecimal(value);
}

/** "12 de set." — a data curta das listas e dos cabeçalhos. */
export function formatShortDate(value: Date | null | undefined): string {
  return value == null ? AUSENTE : diaMes.format(value);
}

/** "12 de set. de 2026" — onde o ano importa (eixo de gráfico, janela
 *  de relatório que atravessa a virada). */
export function formatFullDate(value: Date | null | undefined): string {
  return value == null ? AUSENTE : diaMesAno.format(value);
}

/** "12 de set., 14:30" — quando a hora é o que distingue as linhas. */
export function formatDateTime(value: Date | null | undefined): string {
  return value == null ? AUSENTE : diaMesHora.format(value);
}
